import { FC, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useFormikContext } from 'formik';
import {
  InputSelect,
  InputText,
  InputTextArea,
} from '@farmshare/ui-components';
import type { Primal } from '../../types';

interface FormValues {
  name: string;
  description?: string;
  animalType: string;
  primals: Primal[];
}

/**
 * Form for editing capabilities basic information including primal management
 * Used with useModal for capabilities settings
 * @returns Capabilities info form component
 */
export const CatalogInfoForm: FC = () => {
  const { values, setFieldValue } = useFormikContext<FormValues>();
  const [newPrimalName, setNewPrimalName] = useState('');

  const animalTypeOptions = [
    { value: 'beef', label: 'Beef' },
    { value: 'pork', label: 'Pork' },
    { value: 'lamb', label: 'Lamb' },
    { value: 'chicken', label: 'Chicken' },
    { value: 'other', label: 'Other' },
  ];

  const handleAddPrimal = () => {
    if (!newPrimalName.trim()) return;

    const newPrimal: Primal = {
      id: crypto.randomUUID(),
      title: newPrimalName.trim(),
      type: 'primal',
      sortOrder: values.primals?.length || 0,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    setFieldValue('primals', [...(values.primals || []), newPrimal]);
    setNewPrimalName('');
  };

  const handleRemovePrimal = (id: string) => {
    setFieldValue('primals', values.primals?.filter((p) => p.id !== id) || []);
  };

  return (
    <Stack spacing={3}>
      <InputText label="Capabilities Name" nameOverride="name" required />

      <InputSelect
        label="Animal Type"
        nameOverride="animalType"
        options={animalTypeOptions}
        required
      />

      <InputTextArea label="Description" nameOverride="description" rows={3} />

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Primals
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 2 }}>
          Define the main sections of the animal (e.g., Chuck, Rib, Loin). These
          will appear as tabs.
        </Typography>

        <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <TextField
              size="small"
              placeholder="Enter primal name"
              value={newPrimalName}
              onChange={(e) => setNewPrimalName(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddPrimal();
                }
              }}
              fullWidth
            />
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddPrimal}
              disabled={!newPrimalName.trim()}
              variant="contained"
              size="small"
            >
              Add
            </Button>
          </Stack>

          {values.primals && values.primals.length > 0 ? (
            <Stack spacing={1}>
              {values.primals.map((primal) => (
                <Paper key={primal.id} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="body2">{primal.title}</Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleRemovePrimal(primal.id)}
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary" align="center">
              No primals added yet
            </Typography>
          )}
        </Paper>
      </Box>
    </Stack>
  );
};
